import Vue from 'vue';
import api from '@/services/api';
import { make } from 'vuex-pathify';
import { groupByProperty } from '@/assets/js/utility';

const state = {
  cases: {},
};

const getters = {
  getFeatureCases: state => (dataSourceName, featureId) => {
    return state.cases?.[dataSourceName]?.[featureId] || [];
  },
  getCasesDatasourceName: (_, __, rootState) => {
    return rootState.datasourceSettings.selectedDataSources.find(e => e.name === 'cases_datasource')?.datasource || '';
  },
  getCasesLayerId: (_, __, rootState) => {
    return (
      rootState.datasourceSettings.selectedDataSources.find(e => e.name === 'cases_datasource')?.settings.layer_id ||
      null
    );
  },
  getDocumentsDataSourceName: (_, __, rootState) => {
    return (
      rootState.datasourceSettings.selectedDataSources.find(e => e.name === 'case_documents_datasource')?.datasource ||
      ''
    );
  },
  getDocumentsLayerId: (_, __, rootState) => {
    return (
      rootState.datasourceSettings.selectedDataSources.find(e => e.name === 'case_documents_datasource')?.settings
        .layer_id || null
    );
  },
  getCasesType: (state, getters) => dataSourceName => {
    if (dataSourceName === getters.getCasesDatasourceName) return 'cases';
    else if (dataSourceName === getters.getDocumentsDataSourceName) return 'documents';
    else return 'other';
  },
  getCasesGroupTitle: (_, getters, rootState) => (type, value) => {
    if (type === 'cases') return rootState.layers.metadata[getters.getCasesDatasourceName]?.verbose_name || '';
    else if (type === 'documents')
      return rootState.layers.metadata[getters.getDocumentsDataSourceName]?.verbose_name || '';
    else return rootState.layers.metadata[value]?.verbose_name || value;
  },
};

const mutations = {
  ...make.mutations(state),
  SET_CASES: (state, { dataSourceName, featureId, cases }) => {
    if (!state.cases[dataSourceName]) {
      Vue.set(state.cases, dataSourceName, {});
    }
    Vue.set(state.cases[dataSourceName], featureId, cases);
  },
};

const actions = {
  async getCases({ dispatch, getters }, { dataSourceName, featureId } = {}) {
    const type = getters.getCasesType(dataSourceName);
    if (type === 'cases') await dispatch('getCasesForCase', { dataSourceName, featureId });
    else if (type === 'documents') await dispatch('getCasesForDocument', { dataSourceName, featureId });
    else await dispatch('getCasesForOther', { dataSourceName, featureId });
  },
  async getCasesForCase({ commit, getters }, { dataSourceName, featureId } = {}) {
    const r = await api.get(`v2/cases/case/${featureId}`);
    const { case_documents: documents = [], features = [] } = r.data.data;
    const groupedDocuments = {
      title: getters.getCasesGroupTitle('documents'),
      dataSourceName: getters.getDocumentsDataSourceName,
      documents: documents.map(doc => ({ ...doc, dataSourceName: getters.getDocumentsDataSourceName })),
    };
    const groupedFeatures = Object.entries(groupByProperty(features, 'data_source_name')).reduce(
      (total, [dataSourceName, features]) => {
        total.push({ title: getters.getCasesGroupTitle('other', dataSourceName), dataSourceName, features });
        return total;
      },
      []
    );
    commit('SET_CASES', { dataSourceName, featureId, cases: [groupedDocuments, ...groupedFeatures] });
  },
  async getCasesForDocument({ commit, getters }, { dataSourceName, featureId } = {}) {
    const r = await api.get(`v2/cases/document/${featureId}`);
    const { cases = [], features = [] } = r.data.data;
    const groupedCases = {
      title: getters.getCasesGroupTitle('cases'),
      dataSourceName: getters.getCasesDatasourceName,
      cases,
    };
    const groupedFeatures = Object.entries(groupByProperty(features, 'data_source_name')).reduce(
      (total, [dataSourceName, features]) => {
        total.push({ title: getters.getCasesGroupTitle('other', dataSourceName), dataSourceName, features });
        return total;
      },
      []
    );
    commit('SET_CASES', {
      dataSourceName,
      featureId,
      cases: [...(groupedCases.cases.length ? [groupedCases] : []), ...groupedFeatures],
    });
  },
  async getCasesForOther({ commit, getters }, { dataSourceName, featureId } = {}) {
    const r = await api.get(`v2/cases/feature/${dataSourceName}/${featureId}`);
    const cases =
      r.data.data?.map(item => {
        const { case_documents: documents = [], ...rest } = item;
        return {
          ...rest,
          documents: documents.map(doc => ({ ...doc, dataSourceName: getters.getDocumentsDataSourceName })),
          dataSourceName: getters.getCasesDatasourceName,
        };
      }) || [];
    commit('SET_CASES', { dataSourceName, featureId, cases });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
